import { useMemo, useState, useRef, useEffect, useLayoutEffect } from 'react';

import removeAccents from 'remove-accents';

import './App.css';
import loading from './loading.gif';
import baby from './baby.gif';
import coin from './coin.jpg';
import coinsound from './coin.mp3';
import empty from './empty.webp';
import mushroom from './mushroom.png';
import personPixel from './person-pixel.svg';
import marauder from './marauder.svg';

import Rooms from './components/Rooms';
import Peeps from './components/Peeps';
import MarauderMap from './components/MarauderMap';
import Dashboard from './components/Dashboard';
import Roadmap from './components/Roadmap';
import WhatsNew from './components/WhatsNew';
import { mockDevices, mockBookingsToday } from './mockData';
import { buildDisplayNames, toSoundKey } from './modules/format-name';
import { captureFlightRects, flipFromPending } from './modules/flightAnimation';
import { getOccupantIdentitiesInMeetingRooms } from './modules/roomMatching';
import { parsePeople, deviceMatchesPeople } from './modules/peopleFilter';
import { WHATS_NEW_ENTRIES } from './whatsNewEntries';
import { getRecentEntries, getEntriesToAutoShow, readLastSeenId, markSeen } from './modules/whatsNew';

const UNICATOR_HOST = 'https://unicator.stendahls.dev';
// const UNICATOR_HOST = 'http://localhost:4000';

const USE_MOCK_DATA = new URLSearchParams( window.location.search ).get( 'mock' ) === '1';

// Navigation keeps the current query string (e.g. ?mock=1, ?debug=1) intact —
// plain pushState(path) would otherwise drop it on every view switch.
const pushPath = ( path ) => {
    window.history.pushState( null, null, `${ path }${ window.location.search }` );
};

// Only show people that have been associated in the last 6 hours
const MAX_AVAILABLE_TIME = 6 * 60 * 60;

const timeout = function timeout( ms, promise ) {
    return new Promise( ( resolve, reject ) => {
        setTimeout( () => {
            reject( new Error( 'FETCH_TIMEOUT' ) );
        }, ms );

        promise.then( resolve, reject );
    } );
};

const normaliseIdentifier = ( identifier ) => {
    return removeAccents( identifier.toLowerCase().replace( /[.-]/g, ' ' ) );
};

const App = function App() {
    const [filter, setFilter] = useState( '' );
    const [bookingsToday, setBookingsToday] = useState( [] );
    const [uniqueUsers, setUniqueUsers] = useState(0);
    const [onPremise, setOnPremise] = useState(true);
    const [view, setView] = useState(window.location.pathname.replace('/', '') || 'peeps');
    const [urlParams] = useState(new URLSearchParams( window.location.search ))
    const [people, setPeople] = useState( () => parsePeople( new URLSearchParams( window.location.search ).get( 'people' ) ) );
    const [channel, setChannel] = useState( () => new URLSearchParams( window.location.search ).get( 'channel' ) || '' );
    const [allDevices, setAllDevices] = useState([]);
    const filterInput = useRef( null );
    const coinButton = useRef( null );
    const coinPlayer = useRef( null );
    const [randomPeep, setRandomPeep] = useState( false );
    const [isLoading, setIsLoading] = useState(false);
    const [theme, setTheme] = useState(() => localStorage.getItem('lolcator-theme') || 'io');
    const [sortMode, setSortMode] = useState(() => localStorage.getItem('lolcator-sort') || 'abc');
    const appRootRef = useRef( null );
    // The entries currently shown in the overlay (null = closed). It opens on
    // its own with unseen new features, or from the footer with all recent ones.
    const [whatsNewEntries, setWhatsNewEntries] = useState( () => {
        const unseen = getEntriesToAutoShow( WHATS_NEW_ENTRIES, readLastSeenId() );

        return unseen.length > 0 ? unseen : null;
    } );
    const recentWhatsNew = useMemo( () => getRecentEntries( WHATS_NEW_ENTRIES ), [] );

    const handleWhatsNewClose = () => {
        markSeen( whatsNewEntries );
        setWhatsNewEntries( null );
    };

    const handleThemeToggle = () => {
        const next = theme === 'io' ? 'classic' : 'io';
        setTheme(next);
        localStorage.setItem('lolcator-theme', next);
    };

    const getMeetingRoomOccupantIdentities = () => {
        return getOccupantIdentitiesInMeetingRooms( allDevices, bookingsToday );
    };

    const handleSortModeChange = ( mode ) => {
        if ( view === 'rooms' ) {
            captureFlightRects( appRootRef.current, getMeetingRoomOccupantIdentities() );
        }

        setSortMode(mode);
        localStorage.setItem('lolcator-sort', mode);
        setView('peeps');
        setFilter('');
        pushPath('/');
    };

    const handleRoomsModeChange = () => {
        captureFlightRects( appRootRef.current, getMeetingRoomOccupantIdentities() );
        setView('rooms');
        setFilter('');
        pushPath('/rooms');
    };

    useEffect(() => {
        document.body.style.backgroundColor = theme === 'classic' ? '#000000' : '#EBE8E3';
        document.body.style.color = theme === 'classic' ? '#f9f9f9' : '#242424';
        document.body.style.fontFamily = theme === 'classic'
            ? "'Graphik Web', sans-serif"
            : "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif";
    }, [theme]);

    useLayoutEffect(() => {
        flipFromPending( appRootRef.current );
    }, [ view, bookingsToday ]);

    const handleFilterChange = ( event ) => {
        setFilter(normaliseIdentifier( event.target.value ));
        setRandomPeep(false);

        if ( view !== 'peeps' ) {
            if ( view === 'rooms' ) {
                captureFlightRects( appRootRef.current, getMeetingRoomOccupantIdentities() );
            }

            setView('peeps');
            pushPath('/');
        }
    };

    // Built from everyone online, not just the filtered list, so a person's
    // name doesn't change when a search narrows the list down to them.
    const displayNames = useMemo(() => {
        return buildDisplayNames( allDevices.map( ( device ) => device.identity ) );
    }, [allDevices]);

    const validDevices = useMemo(() => {
        let allValidDevices = allDevices.filter( ( device ) => {
            if ( normaliseIdentifier( device.identity ).includes( filter ) ) {
                return true;
            }

            // Also match spelling variants, so "oskar" finds Oscar and vice versa.
            if ( toSoundKey( normaliseIdentifier( device.identity ) ).includes( toSoundKey( filter ) ) ) {
                return true;
            }

            if ( device.ap.location.indexOf( filter ) === 0 ) {
                return true;
            }

            return false;
        } );

        if(randomPeep){
            allValidDevices = allValidDevices.slice(randomPeep, randomPeep + 1);
        }

        return allValidDevices;
    }, [filter, allDevices, randomPeep]);

    // Counted on all devices so the search field doesn't change "x av y".
    const presentPeopleCount = useMemo( () => {
        return allDevices.filter( ( device ) => deviceMatchesPeople( device, people ) ).length;
    }, [allDevices, people] );

    // People named in ?people= are framed at the top, everyone else follows below.
    const [teamDevices, otherDevices] = useMemo( () => {
        if ( people.length === 0 ) {
            return [ [], validDevices ];
        }

        return [
            validDevices.filter( ( device ) => deviceMatchesPeople( device, people ) ),
            validDevices.filter( ( device ) => !deviceMatchesPeople( device, people ) ),
        ];
    }, [validDevices, people] );

    const handleClearPeople = () => {
        const params = new URLSearchParams( window.location.search );
        params.delete( 'people' );
        params.delete( 'channel' );
        const query = params.toString();

        window.history.replaceState( null, null, `${ window.location.pathname }${ query ? `?${ query }` : '' }` );
        setPeople( [] );
        setChannel( '' );
    };

    const handleCoinClick = () => {
        coinButton.current.classList.add('coin-clicked');
        coinPlayer.current.play();

        setRandomPeep(Math.floor(Math.random() * Math.floor(allDevices.length)));
        setView('peeps');
        pushPath('/');

        setTimeout( () => {
            coinButton.current.classList.remove('coin-clicked');
        }, 200 );
    }

    const handleTitleClick = () => {
        setView('peeps');
        setSortMode('abc');
        localStorage.setItem('lolcator-sort', 'abc');
        pushPath('/');
        setFilter('');
        setRandomPeep(false);
    };

    const handleRoadmapClick = () => {
        setView('roadmap');
        pushPath('/roadmap');
    };

    const updateData = () => {
        setIsLoading(true);

        if ( USE_MOCK_DATA ) {
            setAllDevices( mockDevices );
            setUniqueUsers( mockDevices.length );
            setIsLoading(false);
            document.querySelector( '.App' ).classList.add( 'loaded' );

            return;
        }

        timeout( 5000,
            fetch( `${ UNICATOR_HOST }/graphql`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify( { query: `{
                      devices {
                        assocTime
                        identity
                        firstSeen
                        lastSeen
                        hostname
                        mac
                        isPersonal
                        locator
                        ap {
                          name
                          location
                        }
                      }
                    }` } ),
                }
            )
        )
        .then( ( response ) => {
            return response.json();
        } )
        .then( ( response ) => {
            const actualDevices = {};

            const devices = response.data.devices
                    .filter( ( device ) => {
                        return device.isPersonal;
                    } )
                    .filter( ( device ) => {
                        return new Date().getTime() / 1000 - device.assocTime < MAX_AVAILABLE_TIME;
                    } )
                    .sort( ( a, b ) => {
                        return a.identity.localeCompare( b.identity );
                    } );

            devices.map( ( device ) => {
                if ( !actualDevices[ device.identity ] ) {
                    actualDevices[ device.identity ] = device;

                    return false;
                }

                if ( actualDevices[ device.identity ].lastSeen < device.lastSeen ) {
                    actualDevices[ device.identity ] = device;

                    return true;
                }

                return true;
            } );

            devices.map( ( device ) => {
                if ( !actualDevices[ device.identity ] ) {
                    actualDevices[ device.identity ] = device;

                    return false;
                }

                if ( actualDevices[ device.identity ].lastSeen < device.lastSeen ) {
                    actualDevices[ device.identity ] = device;

                    return true;
                }

                return true;
            } );

            setAllDevices(Object.values( actualDevices ));

            const logins = Object.values( actualDevices ).map( ( device ) => {
                return device.identity;
            } );

            setUniqueUsers([ ...logins.filter((v, i, a) => a.indexOf(v) === i) ].length);
            setIsLoading(false);

            document.querySelector( '.App' ).classList.add( 'loaded' );
        } )
        .catch( ( fetchError  ) => {
            if ( fetchError.message === 'FETCH_TIMEOUT' ) {
                setIsLoading(false);
                setOnPremise( false );
            }
        } );
    }

    const updateRoomData = () => {
        if ( USE_MOCK_DATA ) {
            setBookingsToday( mockBookingsToday );

            return;
        }

        timeout( 5000,
            fetch( `${ UNICATOR_HOST }/graphql`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify( { query: `{
                        bookingsToday {
                            identifier
                            displayName
                            bookings {
                                end
                                organizer
                                start
                                subject
                            }
                        }
                    }` } ),
                }
            )
        )
        .then( ( response ) => {
            return response.json();
        } )
        .then( ( response ) => {
            setBookingsToday(response.data.bookingsToday);
        } )
        .catch( ( fetchError  ) => {
            if ( fetchError.message === 'FETCH_TIMEOUT' ) {
                setOnPremise(false);
            }
        } );
    };

    useEffect(() => {
        (async () => {
            updateData();
            updateRoomData();
        })();

        const updateInterval = setInterval( () => {
            updateData();
            updateRoomData();
        }, 60000 );

        return () => {
            clearInterval( updateInterval );
        };
    },[]);

    const getRendering = () => {
        if ( !onPremise ) {
            return <div
                className = { 'offline' }
            >
                <img
                    alt = { 'Offline' }
                    src = { baby }
                />
                <p
                    className = { 'offline-title' }
                >
                    { 'Du måste vara inne på Stendahls hörru!' }
                </p>
                <p
                    className = { 'offline-title' }
                >
                    { 'Kom in, ta en fika och försök igen <3' }
                </p>
            </div>
        }

        if ( isLoading && allDevices.length === 0 ) {
            return <div
                className = { 'message-wrapper' }
            >
                <img
                    alt = { 'Loading data' }
                    src = { loading }
                />
            </div>
        }

        if ( validDevices.length === 0 ) {
            return <div
                className = { 'message-wrapper' }
            >
                <img
                    alt = { 'Nothing here' }
                    src = { empty }
                />
            </div>
        }

        if ( people.length > 0 && sortMode !== 'floor' ) {
            return <>
                <section
                    className = { 'people-group' }
                >
                    <div
                        className = { 'people-banner' }
                    >
                        <span>
                            { `${ presentPeopleCount } av ${ people.length }${ channel ? ` i ${ channel }` : '' } är i huset` }
                        </span>
                        <button
                            type = { 'button' }
                            onClick = { handleClearPeople }
                        >
                            { 'Rensa filter' }
                        </button>
                    </div>
                    { teamDevices.length > 0
                        ? <Peeps
                            validDevices = {teamDevices}
                            urlParams = {urlParams}
                            sortMode = {sortMode}
                        />
                        : <p
                            className = { 'people-group-empty' }
                        >
                            { 'Ingen av dem är i huset just nu' }
                        </p> }
                </section>
                { otherDevices.length > 0 && <Peeps
                    validDevices = {otherDevices}
                    urlParams = {urlParams}
                    sortMode = {sortMode}
                /> }
            </>;
        }

        return <Peeps
            validDevices = {validDevices}
            urlParams = {urlParams}
            sortMode = {sortMode}
            displayNames = {displayNames}
        />
    };

    const connectionState = !onPremise
        ? 'offline'
        : ( isLoading && allDevices.length === 0 ) ? 'connecting' : 'online';

    const connectionLabel = {
        offline: 'Not connected',
        connecting: 'Connecting',
        online: `Online · ${ uniqueUsers } ${ uniqueUsers === 1 ? 'person' : 'personer' }`,
    }[ connectionState ];

    const matchingRooms = useMemo( () => {
        return bookingsToday.filter((room) => {
            if(normaliseIdentifier(room.displayName).includes(normaliseIdentifier(filter))){
                return true;
            }

            if(room.identifier.toLowerCase().includes(filter.toLocaleLowerCase())){
                return true;
            }

            return false;
        });
    },[bookingsToday, filter]);

    return (
        <div
            className={`App${theme === 'classic' ? ' theme-classic' : ''}`}
            ref = { appRootRef }
        >
            <div
                className = { 'top-bar' }
            >
                <h1
                    className = { 'logo-heading' }
                    onClick = { handleTitleClick }
                    title = { `Just nu är det ungefär ${ uniqueUsers } stendarlings i huset` }
                >
                    { 'L0LC4tor' }
                </h1>
                { ( view === 'peeps' || view === 'rooms' || view === 'roadmap' ) && <div
                    className = { 'sort-toggle-pill' }
                    role = { 'group' }
                    aria-label = { 'Sortera personer' }
                >
                    <button
                        type = { 'button' }
                        className = { `sort-toggle-btn${ view === 'peeps' && sortMode === 'abc' ? ' active' : '' }` }
                        onClick = { () => handleSortModeChange('abc') }
                        aria-pressed = { view === 'peeps' && sortMode === 'abc' }
                        title = { 'Personer' }
                    >
                        { theme === 'classic'
                            ? <img
                                alt = { 'Personer' }
                                className = { 'sort-toggle-icon' }
                                src = { personPixel }
                            />
                            : 'Personer' }
                    </button>
                    <button
                        type = { 'button' }
                        className = { `sort-toggle-btn${ view === 'peeps' && sortMode === 'floor' ? ' active' : '' }` }
                        onClick = { () => handleSortModeChange('floor') }
                        aria-pressed = { view === 'peeps' && sortMode === 'floor' }
                        title = { 'Våningsplan' }
                    >
                        { theme === 'classic'
                            ? <img
                                alt = { 'Våningsplan' }
                                className = { 'sort-toggle-icon sort-toggle-icon-marauder' }
                                src = { marauder }
                            />
                            : 'Våningsplan' }
                    </button>
                    <button
                        type = { 'button' }
                        className = { `sort-toggle-btn${ view === 'rooms' ? ' active' : '' }` }
                        onClick = { handleRoomsModeChange }
                        aria-pressed = { view === 'rooms' }
                        title = { 'Mötesrum' }
                    >
                        { theme === 'classic'
                            ? <img
                                alt = { 'Mötesrum' }
                                className = { 'sort-toggle-icon' }
                                src = { mushroom }
                            />
                            : 'Mötesrum' }
                    </button>
                </div> }
                { ( view === 'peeps' || view === 'rooms' || view === 'roadmap' ) && <input
                    type = { 'search' }
                    value = { filter }
                    onChange = { handleFilterChange }
                    placeholder = { 'Hitta...' }
                    aria-label = { 'Hitta...' }
                    className = { 'peeps-search' }
                    ref = { filterInput }
                    autoFocus
                /> }
            </div>
            <header className="App-header">
                <h1
                    onClick = { handleTitleClick }
                    title = { `Just nu är det ungefär ${ uniqueUsers } stendarlings i huset` }
                >
                    { 'L0LC4tor' }
                </h1>
                <button
                    className = "theme-toggle"
                    onClick = { handleThemeToggle }
                    title = { theme === 'io' ? 'Byt till klassiskt tema' : 'Byt till iO-tema' }
                >
                    { theme === 'io' ? 'Classic' : 'iO' }
                </button>
                <a
                    href = "slack://user?team=T034ZQLT0&id=U02CRLC7A5C"
                    className = "remove-link"
                >
                    Jag vill inte synas här!
                </a>
            </header>
            {view === 'rooms' && <Rooms
                bookingsToday = { matchingRooms }
                allDevices = { allDevices }
                displayNames = { displayNames }
            />}
            {view === 'marauder' && <MarauderMap
                validDevices = {validDevices}
                urlParams = {urlParams}
                displayNames = { displayNames }
            />}
            {view === 'dashboard' && <Dashboard />}
            {view === 'roadmap' && <Roadmap />}
            {view === 'peeps' && getRendering() }
            <footer className="app-footer">
                <div
                    className = { 'connection-status' }
                >
                    <span
                        className = { `connection-dot connection-dot-${ connectionState }` }
                    />
                    <span
                        className = { 'connection-label' }
                    >
                        { connectionLabel }
                    </span>
                </div>
                <button
                    type = { 'button' }
                    className = { 'footer-link' }
                    onClick = { handleThemeToggle }
                >
                    { theme === 'io' ? 'Byt till klassiskt läge' : 'Byt till iO-läge' }
                </button>
                <button
                    type = { 'button' }
                    className = { 'footer-link' }
                    onClick = { handleRoadmapClick }
                >
                    Roadmap
                </button>
                { recentWhatsNew.length > 0 && (
                    <button
                        type = { 'button' }
                        className = { 'footer-link' }
                        onClick = { () => setWhatsNewEntries( recentWhatsNew ) }
                    >
                        Nytt i appen
                    </button>
                ) }
                <a
                    href = "slack://user?team=T034ZQLT0&id=U02CRLC7A5C"
                    className = "footer-link"
                >
                    Jag vill inte synas här!
                </a>
                <img
                    alt = 'Slumpa fram en stendarling'
                    className = 'coin coin-easter-egg'
                    onClick = { handleCoinClick }
                    title = 'Slumpa fram en stendarling'
                    src = { coin }
                    ref = { coinButton }
                />
                <audio
                    ref = { coinPlayer }
                >
                    <source
                        src = { coinsound }
                        type = 'audio/mpeg'
                    />
                </audio>
            </footer>
            <div className="flames" />
            { whatsNewEntries && (
                <WhatsNew
                    entries = { whatsNewEntries }
                    onClose = { handleWhatsNewClose }
                />
            ) }
        </div>
    );
}

export default App;
